// TemplateUI.tsx — the presentational pieces of the create-template page:
// live preview, basics inputs, the message composer, the personalization breakdown,
// the quiet named-vs-numbered control, and the action bar.

import { useState } from 'react'
import { Input, Select, Switch, Popover } from 'antd'
import {
  ClipboardDocumentIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ArrowTopRightOnSquareIcon,
  PhoneIcon
} from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'
import { PillEditor } from './PillEditor'
import {
  TemplateDraft,
  TemplateDraftController,
  Category,
  ParameterFormat,
  ButtonType,
  FieldSegment,
  TemplateButton,
  BUTTON_TYPE_OPTIONS,
  MAX_BUTTONS,
  MAX_BUTTON_TEXT,
  makeButton,
  LANGUAGE_OPTIONS,
  segmentsToText,
  collectVariables
} from './templateModel'

/* --------------------------- live WhatsApp preview --------------------------- */

function ButtonPreviewIcon({ type }: { type: ButtonType }) {
  if (type === 'URL') return <ArrowTopRightOnSquareIcon className="size-4" />
  if (type === 'PHONE_NUMBER') return <PhoneIcon className="size-4" />
  return <ArrowUturnLeftIcon className="size-4" />
}

export function WhatsAppPreview({ draft, className = '' }: { draft: TemplateDraft; className?: string }) {
  const header = draft.headerEnabled ? draft.headerText.trim() : ''
  const body = segmentsToText(draft.bodySegments, 'preview').trim()
  const footer = draft.footerText.trim()
  const buttons = (draft.buttons || []).filter(b => b.text.trim())
  const empty = !header && !body && !footer && buttons.length === 0

  return (
    <div className={className}>
      <p className="mb-2 text-xs font-medium text-gray-400">Preview · sample data</p>
      <div
        className="rounded-2xl p-3 shadow-inner"
        style={{
          backgroundColor: '#E5DDD5',
          backgroundImage: 'radial-gradient(rgba(0,0,0,0.035) 1px, transparent 0)',
          backgroundSize: '18px 18px'
        }}
      >
        <div className="max-w-[85%] rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm">
          {empty ? (
            <p className="text-sm italic text-gray-400">Your message will appear here…</p>
          ) : (
            <>
              {header && (
                <p className="mb-1 whitespace-pre-wrap text-sm font-bold text-gray-900">{header}</p>
              )}
              {body && <p className="whitespace-pre-wrap text-sm text-gray-800">{body}</p>}
              {footer && <p className="mt-1 whitespace-pre-wrap text-xs text-gray-400">{footer}</p>}
            </>
          )}
          <div className="mt-1 flex items-center justify-end gap-1">
            <span className="text-[10px] text-gray-400">12:24</span>
            <CheckIcon className="size-3 text-sky-500" />
          </div>
        </div>

        {/* Buttons render as tappable rows beneath the bubble, like WhatsApp does. */}
        {buttons.length > 0 && (
          <div className="mt-1 max-w-[85%] space-y-1">
            {buttons.map(b => (
              <div
                key={b.id}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-medium text-sky-600 shadow-sm"
              >
                <ButtonPreviewIcon type={b.type} />
                <span className="truncate">{b.text || 'Button'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ------------------------------- buttons editor -------------------------------
   Repeatable object-group input (no existing primitive fits: InputFieldList is
   string-only). Each row is a button {type, text, url?, phone?} with type-specific
   fields. Serialised to a BUTTONS component by buildPayload(). */

export function ButtonsEditor({
  draft,
  patch,
  errors = {}
}: {
  draft: TemplateDraft
  patch: (p: Partial<TemplateDraft>) => void
  errors?: Record<string, string>
}) {
  const buttons = draft.buttons || []

  const add = () => patch({ buttons: [...buttons, makeButton('QUICK_REPLY')] })
  const remove = (id: string) => patch({ buttons: buttons.filter(b => b.id !== id) })
  const update = (id: string, p: Partial<TemplateButton>) =>
    patch({ buttons: buttons.map(b => (b.id === id ? { ...b, ...p } : b)) })

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">Buttons (optional)</label>
        {buttons.length < MAX_BUTTONS && (
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700"
          >
            <PlusIcon className="size-3.5" />
            Add button
          </button>
        )}
      </div>

      {buttons.length === 0 ? (
        <p className="text-xs text-gray-400">
          Add quick replies, a website link, or a call button under your message.
        </p>
      ) : (
        <div className="space-y-2">
          {buttons.map(b => (
            <div key={b.id} className="rounded-lg border border-gray-200 p-2">
              <div className="flex items-center gap-2">
                <Select
                  value={b.type}
                  onChange={(v: ButtonType) => update(b.id, { type: v })}
                  options={BUTTON_TYPE_OPTIONS}
                  size="small"
                  style={{ width: 160 }}
                />
                <Input
                  size="small"
                  placeholder="Button text"
                  value={b.text}
                  maxLength={MAX_BUTTON_TEXT}
                  onChange={e => update(b.id, { text: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => remove(b.id)}
                  aria-label="Remove button"
                  className="shrink-0 text-gray-400 hover:text-red-500"
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
              {b.type === 'URL' && (
                <Input
                  className="mt-2"
                  size="small"
                  placeholder="https://example.com"
                  value={b.url}
                  onChange={e => update(b.id, { url: e.target.value })}
                />
              )}
              {b.type === 'PHONE_NUMBER' && (
                <Input
                  className="mt-2"
                  size="small"
                  placeholder="+91 98765 43210"
                  value={b.phoneNumber}
                  onChange={e => update(b.id, { phoneNumber: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {errors.buttons && <p className="mt-1 text-xs text-red-500">{errors.buttons}</p>}
    </div>
  )
}

/* ------------------------- name / category / language ------------------------- */

const CATEGORY_OPTIONS = [
  { label: 'Marketing', value: 'MARKETING' },
  { label: 'Utility', value: 'UTILITY' },
  { label: 'Authentication', value: 'AUTHENTICATION' }
]

export function TemplateBasics({
  draft,
  patch,
  errors = {}
}: {
  draft: TemplateDraft
  patch: (p: Partial<TemplateDraft>) => void
  errors?: Record<string, string>
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Template name</label>
        <Input
          placeholder="e.g. Order confirmation"
          value={draft.name}
          onChange={e => patch({ name: e.target.value })}
          status={errors.name ? 'error' : ''}
          maxLength={512}
        />
        {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Category</label>
        <Select
          value={draft.category}
          onChange={(v: Category) => patch({ category: v })}
          options={CATEGORY_OPTIONS}
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Language</label>
        <Select
          value={draft.language}
          onChange={v => patch({ language: v })}
          options={LANGUAGE_OPTIONS}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
        />
      </div>
    </div>
  )
}

/* ------------------------ header / body / footer composer ------------------------ */

export function MessageComposer({ ctrl }: { ctrl: TemplateDraftController }) {
  const { draft, patch, setBody, catalog, registerCustom, validation } = ctrl
  const errors = validation.errors

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600">Header (optional)</label>
          <Switch
            size="small"
            checked={draft.headerEnabled}
            onChange={v => patch({ headerEnabled: v })}
          />
        </div>
        {draft.headerEnabled && (
          <Input
            placeholder="e.g. Order confirmed 🎉"
            value={draft.headerText}
            onChange={e => patch({ headerText: e.target.value })}
            status={errors.header ? 'error' : ''}
            maxLength={60}
          />
        )}
        {errors.header && <p className="mt-1 text-xs text-red-500">{errors.header}</p>}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Message</label>
        <PillEditor
          segments={draft.bodySegments}
          onChange={setBody}
          catalog={catalog}
          onRegisterCustom={registerCustom}
          placeholder="Write your message… tap “Add personalization” to fill in each person’s details."
        />
        {errors.body && <p className="mt-1 text-xs text-red-500">{errors.body}</p>}
        {validation.messages.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {validation.messages.map((m, i) => (
              <li key={i} className="text-xs text-amber-600">
                {m}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Footer (optional)</label>
        <Input
          placeholder="e.g. Reply STOP to opt out"
          value={draft.footerText}
          onChange={e => patch({ footerText: e.target.value })}
          maxLength={60}
        />
      </div>
    </div>
  )
}

/* --------------------------- personalization breakdown ---------------------------
   Shows the ACTUAL variable name next to its friendly label, so the user knows what
   to name their sheet columns. Mirrors buildPayload():
     NAMED      → deduped by variable name  ("First name" → first_name)
     POSITIONAL → every pill in document order ("First name" → {{1}}) */

interface DetailRow {
  label: string
  token: string
}

function buildRows(draft: TemplateDraft): DetailRow[] {
  const fields = draft.bodySegments.filter(s => s.type === 'field') as FieldSegment[]
  if (draft.parameterFormat === 'POSITIONAL') {
    return fields.map((f, i) => ({ label: f.label, token: `{{${i + 1}}}` }))
  }
  return collectVariables(draft.bodySegments).map(v => ({ label: v.label, token: v.name }))
}

export function PersonalizationDetails({ draft }: { draft: TemplateDraft }) {
  const [copied, setCopied] = useState(false)
  const rows = buildRows(draft)
  const isNamed = draft.parameterFormat !== 'POSITIONAL'

  if (rows.length === 0) {
    return (
      <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs text-gray-500">
        Everyone gets exactly this message — no details are filled in per person.
      </div>
    )
  }

  const copyHeaders = async () => {
    try {
      await navigator.clipboard.writeText(rows.map(r => r.token).join(', '))
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch (e) {
      /* clipboard unavailable — ignore */
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50/70 p-3">
      <p className="text-xs font-medium text-gray-700">Filled in for each person ({rows.length})</p>

      <ul className="mt-2 space-y-1.5">
        {rows.map((r, i) => (
          <li key={`${r.token}-${i}`} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-xs text-gray-600">{r.label}</span>
            <code className="shrink-0 rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-700 ring-1 ring-gray-200">
              {r.token}
            </code>
          </li>
        ))}
      </ul>

      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-200/70 pt-2">
        <p className="text-[11px] leading-4 text-gray-400">
          {isNamed
            ? 'Use these exact names as the column headers in your sheet.'
            : 'Your sheet values are filled in this order.'}
        </p>
        {isNamed && (
          <button
            type="button"
            onClick={copyHeaders}
            className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            {copied ? (
              <>
                <CheckIcon className="size-3 text-green-600" />
                Copied
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="size-3" />
                Copy names
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

/* ------------------- quiet named-vs-numbered control -------------------
   "Setting as a sentence": one muted line stating the default as a FACT, with the
   value as a subtle inline dropdown. No green, no card, no "Recommended" badge — so
   it recedes and the colored pills stay the salient thing. Rendered via antd Popover
   so the menu portals above the dialog and auto-flips (never clipped). */

const VALUE_LABEL: Record<ParameterFormat, string> = {
  NAMED: 'by name',
  POSITIONAL: 'by number'
}

export function VariableStyleInline({
  value,
  onChange
}: {
  value: ParameterFormat
  onChange: (f: ParameterFormat) => void
}) {
  const [open, setOpen] = useState(false)
  const pick = (f: ParameterFormat) => {
    onChange(f)
    setOpen(false)
  }

  const menu = (
    <div className="w-64">
      <StyleRow
        selected={value === 'NAMED'}
        onClick={() => pick('NAMED')}
        title="By name"
        tag="Default"
        hint="Auto-fills from your contacts & sheet columns."
      />
      <StyleRow
        selected={value === 'POSITIONAL'}
        onClick={() => pick('POSITIONAL')}
        title="By number"
        hint="Sends as {{1}}, {{2}} — for tools or imports that expect numbers."
      />
    </div>
  )

  return (
    <p className="mt-3 text-[13px] leading-6 text-gray-500">
      Personalizations are filled in{' '}
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        overlayInnerStyle={{ padding: 4 }}
        content={menu}
      >
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Fill style: ${VALUE_LABEL[value]}. Change`}
          className="inline-flex items-center gap-0.5 rounded-[3px] border-b border-dashed border-gray-400/60 px-0.5 align-baseline text-gray-600 hover:border-solid hover:bg-gray-500/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          style={{ minHeight: 22 }}
        >
          {VALUE_LABEL[value]}
          <ChevronDownIcon className="size-3 text-gray-400" />
        </button>
      </Popover>
    </p>
  )
}

function StyleRow({
  selected,
  onClick,
  title,
  hint,
  tag
}: {
  selected: boolean
  onClick: () => void
  title: string
  hint: string
  tag?: string
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-50"
    >
      <span className="mt-0.5 w-3.5 shrink-0">
        {selected && <CheckIcon className="size-3.5 text-green-600" />}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="text-[13px] font-medium text-gray-800">{title}</span>
          {tag && (
            <span className="rounded bg-gray-100 px-1 py-0.5 text-[10px] font-medium text-gray-500">
              {tag}
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-xs text-gray-400">{hint}</span>
      </span>
    </button>
  )
}

/* ------------------------------- action bar ------------------------------- */

export function CreateActions({
  onCancel,
  onSubmit,
  submitting,
  disabled,
  submitLabel = 'Submit for approval'
}: {
  onCancel: () => void
  onSubmit: () => void
  submitting: boolean
  disabled?: boolean
  submitLabel?: string
}) {
  return (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting || disabled}
        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {submitting ? 'Submitting…' : submitLabel}
      </button>
    </div>
  )
}
